"use client";

import { motion } from "motion/react";
import { WIZARD_STEPS } from "../_logic/constants";
import { WizardStep } from "../_types/wizard-types";

interface WizardProgressBarProps {
  currentStep: WizardStep;
  completedSteps: Set<WizardStep>;
  onStepClick: (step: WizardStep) => void;
}

/** Segmented progress bar. Completed segments are clickable. */
export const WizardProgressBar = ({ currentStep, completedSteps, onStepClick }: WizardProgressBarProps) => {
  // Derived values
  const currentIndex = WIZARD_STEPS.findIndex((s) => s.id === currentStep);

  // Render
  return (
    <nav className="flex gap-1.5 w-full" aria-label="Progress">
      {WIZARD_STEPS.map((step, index) => {
        const isCompleted = completedSteps.has(step.id);
        const isCurrent = step.id === currentStep;
        const isFuture = index > currentIndex && !isCompleted;

        return (
          <button
            key={step.id}
            type="button"
            onClick={() => isCompleted && onStepClick(step.id)}
            disabled={isFuture || isCurrent}
            className={`flex-1 ${isCompleted ? "cursor-pointer" : ""}`}
            aria-current={isCurrent ? "step" : undefined}
            aria-label={`Step ${index + 1}${isCompleted ? " (completed)" : isCurrent ? " (current)" : ""}`}
          >
            <div className="h-2 rounded-full overflow-hidden bg-gray-200">
              {/* Fill uses the themed --indigo-500 (via bg-indigo-500) so enterprise domains recolor it;
                  the empty state is just the gray-200 track showing through at width 0. */}
              <motion.div
                className="h-full rounded-full bg-indigo-500"
                initial={false}
                animate={{ width: isCompleted || isCurrent ? "100%" : "0%" }}
                transition={{ duration: 0.4, ease: "easeOut" }}
              />
            </div>
          </button>
        );
      })}
    </nav>
  );
};
